import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { EnvelopeSimple, Lock, Eye, EyeSlash, Globe } from '@phosphor-icons/react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('messages.requiredField'));
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split min-h-screen">
      {/* Left side - Form */}
      <div className="flex flex-col justify-center px-8 lg:px-16 py-12 bg-background relative">
        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          className="absolute top-6 right-6 flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
          data-testid="login-language-toggle"
        >
          <Globe size={18} weight="duotone" />
          <span className="text-sm font-medium">{language === 'tr' ? 'TR' : 'EN'}</span>
        </button>

        <div className="max-w-md w-full mx-auto">
          <div className="mb-10">
            <h1 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-foreground">
              {t('auth.welcome')}
            </h1>
            <p className="mt-3 text-muted-foreground">
              {t('auth.loginSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                {t('auth.email')}
              </Label>
              <div className="relative">
                <EnvelopeSimple 
                  size={20} 
                  weight="duotone" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@restoran.com"
                  className="h-12 pl-12 input-enhanced"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Lock 
                  size={20} 
                  weight="duotone" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 pr-12 input-enhanced"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md btn-primary"
              data-testid="login-submit"
            >
              {loading ? `${t('common.loading')}` : t('auth.login')}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link 
              to="/register" 
              className="text-primary hover:underline font-medium"
              data-testid="register-link"
            >
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="login-image-side relative hidden md:block">
        <img
          src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80"
          alt="Restaurant kitchen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/60 to-foreground/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white">
            <p className="font-heading text-2xl lg:text-3xl font-light leading-relaxed">
              {language === 'tr' 
                ? '"Başarılı bir restoran, iyi bir mutfaktan değil, iyi bir yönetimden geçer."'
                : '"A successful restaurant comes from good management, not just a good kitchen."'
              }
            </p>
            <footer className="mt-4 text-white/70 text-sm uppercase tracking-widest">
              — {language === 'tr' ? 'Stok Yönetim Sistemi' : 'Stock Management System'}
            </footer>
          </blockquote>
        </div>
      </div>
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-500">
        © 2026 Sergen Sınar • Tüm Hakları Saklıdır
      </div>
    </div>
  );
}
